import {
  accountMutationHeaders,
  readBoundedAccountJson,
} from "./account-state-session-repository.js";
import {
  createAccountOperationId,
  isAccountOperationId,
} from "./account-operation-id.js";
import { decodePasskeyLabelsProjectionV1 } from "./kratos-session-contract.js";

const PASSKEY_LABEL_PATH = "/api/v1/account/passkey-labels";
const RESPONSE_MAX_BYTES = 12 * 1024;
const CREDENTIAL_ID_PATTERN = /^(?:[0-9a-f]{2}){1,512}$/;
const MAX_LABEL_UTF16_UNITS = 80;

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactFields(value, expected) {
  if (!isRecord(value)) return false;
  const actual = Object.keys(value).sort();
  const fields = expected.slice().sort();
  return actual.length === fields.length
    && actual.every((field, index) => field === fields[index]);
}

function apiUrl(apiBaseUrl) {
  const base = new URL(String(apiBaseUrl ?? ""));
  if (
    !["http:", "https:"].includes(base.protocol)
    || base.username
    || base.password
    || base.pathname !== "/"
    || base.search
    || base.hash
  ) {
    throw new TypeError("Passkey-label API base URL must be an HTTP origin.");
  }
  return `${base.origin}${PASSKEY_LABEL_PATH}`;
}

function exactCredentialId(value) {
  if (typeof value !== "string" || !CREDENTIAL_ID_PATTERN.test(value)) {
    throw new TypeError("Passkey credential ID is invalid.");
  }
  return value;
}

export function exactPasskeyLabel(value) {
  if (
    typeof value !== "string"
    || value.length < 1
    || value.length > MAX_LABEL_UTF16_UNITS
    || value.trim() !== value
    || value.includes("\0")
  ) {
    throw new TypeError("Passkey name must be 1–80 characters without surrounding spaces.");
  }
  return value;
}

function acknowledgement(value, operationId) {
  if (
    !hasExactFields(value, ["kind", "operationId", "authoritativeState"])
    || !["applied", "conflict"].includes(value.kind)
    || value.operationId !== operationId
  ) {
    throw new TypeError("Passkey-label acknowledgement is invalid.");
  }
  return Object.freeze({
    kind: value.kind,
    operationId,
    authoritativeState: decodePasskeyLabelsProjectionV1(value.authoritativeState),
  });
}

export function createPasskeyLabelProvider({
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId = createAccountOperationId,
} = {}) {
  if (typeof fetchImpl !== "function" || typeof createOperationId !== "function") {
    throw new TypeError("Passkey-label provider requires fetch and operation identity.");
  }
  const url = apiUrl(apiBaseUrl);

  async function setLabel(session, projection, credentialId, label, { signal } = {}) {
    const authoritative = decodePasskeyLabelsProjectionV1(projection);
    const operationId = createOperationId();
    if (!isAccountOperationId(operationId)) {
      throw new TypeError("Passkey-label operation ID is invalid.");
    }
    const command = {
      schemaVersion: 1,
      operationId,
      credentialId: exactCredentialId(credentialId),
      expectedRevision: authoritative.kind === "ready" ? authoritative.revision : null,
      label: exactPasskeyLabel(label),
    };
    const response = await fetchImpl(url, {
      method: "POST",
      cache: "no-store",
      credentials: "include",
      headers: accountMutationHeaders(session),
      redirect: "error",
      signal,
      body: JSON.stringify(command),
    });
    if (response.status === 401) return Object.freeze({ kind: "signedOut" });
    if (![200, 409].includes(response.status)) {
      throw new Error("Passkey name could not be saved.");
    }
    const decoded = acknowledgement(
      await readBoundedAccountJson(response, "Passkey-label acknowledgement", RESPONSE_MAX_BYTES),
      operationId,
    );
    if (
      (response.status === 200 && decoded.kind !== "applied")
      || (response.status === 409 && decoded.kind !== "conflict")
    ) {
      throw new TypeError("Passkey-label status and acknowledgement disagree.");
    }
    return decoded;
  }

  return Object.freeze({ setLabel });
}
